if (import.meta.hot) {
  import.meta.hot.accept();
}

if (import.meta.env.DEV) {
  document.body.classList.add('debug-mode');
}

import { loadApp, loadModule } from '@/core';
import { eventBus } from '@spon/plugins';

import Header from '@/globals/Header';
import Modal from '@/globals/Modal';
import FadeIn from '@/globals/FadeIn';
import SmoothScroll from '@/globals/SmoothScroll';
// import PixelCanvas from '@/globals/PixelCanvas';
// TextAnimation + PageTransition are loaded after first paint —
// see the requestIdleCallback block below. They pull in gsap/SplitText/Barba
// which together account for ~80KB gzip and aren't needed before idle.

import '@/plugins/logger';
import detectTab from '@/utils/detectTab';
import activeLinks from '@/utils/activeLinks';
import localLinks from '@/utils/localLinks';
import { showMessage } from './ui/messages';

// load from data-behaviours
const app = loadApp(name => import(`./behaviours/${name}.js`), document.body);

loadModule({
  module: Header,
  id: 'header',
  node: document.getElementById('header'),
  keepAlive: true,
});

loadModule({
  module: Modal,
  id: 'modal',
  node: document.body,
  keepAlive: true,
});

loadModule({
  module: FadeIn,
  id: 'fadein',
  node: document.body,
  keepAlive: true,
});

loadModule({
  module: SmoothScroll,
  id: 'smooth-scroll',
  node: document.body,
  keepAlive: true,
});

// loadModule({
// 	module: PixelCanvas,
// 	id: 'pixel-canvas',
// 	node: document.getElementById('webgl'),
// 	keepAlive: true,
// });

// Defer heavy globals past first paint. Both modules dynamic-import their
// animation/transition deps (gsap/SplitText/Barba), so they ship as
// separate chunks fetched in parallel with the main bundle but not
// parse-blocking. requestIdleCallback ensures init runs after the browser
// has finished its critical work; setTimeout is the Safari/older-browser
// fallback (Safari shipped rIC in 16.4 but it's safer to gate it).
const whenIdle = cb =>
  typeof window.requestIdleCallback === 'function'
    ? window.requestIdleCallback(cb, { timeout: 1500 })
    : setTimeout(cb, 1);

whenIdle(() => {
  Promise.all([
    import('@/globals/TextAnimation'),
    import('@/globals/PageTransition'),
  ]).then(([{ default: TextAnimation }, { default: PageTransition }]) => {
    loadModule({
      module: TextAnimation,
      id: 'text-animation',
      node: document.body,
      keepAlive: true,
    });

    loadModule({
      module: PageTransition,
      id: 'page-transition',
      node: document.body,
      keepAlive: true,
    });
  });
});

// Tear down behaviours in outgoing Barba container
eventBus.on('page:exited', () => {
  const container =
    document.querySelector('[data-barba="container"]') || document.body;
  app.unloadContext(container);
});

// Re-hydrate behaviours and refresh utilities after Barba page transition.
// During a transition both old and new containers coexist in the DOM.
// The incoming container is marked with .is-entering by PageTransition.
eventBus.on('page:entered', () => {
  const container =
    document.querySelector('[data-barba="container"].is-entering') ||
    document.querySelector('[data-barba="container"]') ||
    document.body;

  app.hydrate(container);
  activeLinks(window.location.pathname);
  localLinks();

  if (typeof htmx !== 'undefined') {
    htmx.process(container);
  }

  if (window.Formie) {
    window.Formie.initForms();
  }
});

// Formie cache refresh — permanent listener for both full loads and Barba transitions
document.addEventListener('onFormieInit', event => {
  if (window.Formie) {
    window.Formie.refreshForCache(event.detail.formId);
  }
});

// Hydrate document.body so behaviours in the persistent footer (e.g.
// BaseWordmark) are picked up alongside those inside the Barba container.
// Subsequent transitions re-hydrate only the swapped container.
app.hydrate(document.body);

detectTab();
activeLinks(window.location.pathname);
localLinks();

// https://htmx.org/events
if (typeof htmx !== 'undefined') {
  // Fade during HTMX loading

  htmx.on('htmx:beforeSend', ({ detail: { elt } }) => {
    const listing = elt?.closest('[data-sprig-listing]');
    if (listing) {
      listing.classList.add('is-loading');
    }
  });

  // Scroll to top of `sprig` container when interacting; but only when `scroll` is in Param (eg not filtering; but probably pagination)

  htmx.on(
    'htmx:beforeSwap',
    ({
      detail: {
        requestConfig: { triggeringEvent },
      },
      target,
    }) => {
      // Tear down behaviours in the content about to be replaced
      app.unloadContext(target);

      localLinks(target);

      if (triggeringEvent) {
        const { srcElement } = triggeringEvent;
        if (!srcElement) return;

        const shouldScroll = srcElement.closest('[aria-label="Pagination"]');
        if (shouldScroll) {
          eventBus.emit('smoothScroll:scrollTo', { target, offset: -100 });
        }
      }
    }
  );

  // Remove loading state after Sprig swap and re-hydrate behaviours
  htmx.on('htmx:afterSwap', ({ target }) => {
    const listing = target.closest('[data-sprig-listing]');
    if (listing) {
      listing.classList.remove('is-loading');
    }

    app.hydrate(target);
  });

  htmx.on('htmx:responseError', () => {
    showMessage('Something went wrong, please try again.');
  });
}
